import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const t = {
  es: {
    label: "Preguntas Frecuentes",
    title: "Todo lo que necesitas",
    titleAccent: "saber antes de llegar",
    items: [
      {
        q: "¿Puedo llevar bebidas alcohólicas?",
        a: "No, ya que buscamos un espacio mucho más familiar.",
      },
      {
        q: "¿Se admiten mascotas?",
        a: "En el complejo privado es permitido el ingreso de mascotas bajo la responsabilidad de los huéspedes. En los Bungalows no es permitido el ingreso de mascotas.",
      },
      {
        q: "¿Cuál es el procedimiento para reservar?",
        a: "Debes contactarnos vía WhatsApp o llamada telefónica indicándonos las fechas de interés. La reserva se hace con el 50% del total y el otro 50% se cancela 24 horas antes de la llegada. En toda reservación se entrega un depósito de Q500.00 en efectivo al recibir las instalaciones, reembolsable al salir.",
      },
      {
        q: "¿Cuáles son los horarios de check in y check out?",
        a: "Para el check in podemos ser flexibles para poder ayudarte. El horario de salida es a las 15:00 horas. Contáctanos para más información.",
      },
      {
        q: "¿Cuenta con restaurantes?",
        a: "Actualmente no contamos con restaurantes, solo con una pequeña tienda.",
      },
    ],
  },
  en: {
    label: "Common Questions",
    title: "Everything you need",
    titleAccent: "to know before arriving",
    items: [
      {
        q: "Can I bring alcoholic beverages?",
        a: "No, as we aim to maintain a family-friendly environment.",
      },
      {
        q: "Are pets allowed?",
        a: "Pets are allowed in the private complex under guest responsibility. Pets are not allowed in the individual Bungalows.",
      },
      {
        q: "What is the reservation procedure?",
        a: "Contact us via WhatsApp or phone with your desired dates. A 50% deposit is required to confirm, with the remaining 50% due 24 hours before arrival. A Q500 cash security deposit is collected upon arrival and refunded on checkout.",
      },
      {
        q: "What are the check-in and check-out times?",
        a: "Check-in times are flexible — contact us to arrange. Check-out is at 3:00 PM.",
      },
      {
        q: "Do you have restaurants?",
        a: "We currently don't have restaurants, only a small convenience store.",
      },
    ],
  },
};

function FAQItem({ item, isOpen, toggle, idx }) {
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className="border-b border-[#1F3A5E]/8 last:border-0"
    >
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-montserrat font-semibold text-[#1F3A5E] text-base pr-4 group-hover:text-[#8C5E35] transition-colors duration-200">
          {item.q}
        </span>
        <span className="shrink-0 w-8 h-8 rounded-full border border-[#1F3A5E]/20 flex items-center justify-center text-[#1F3A5E]">
          {isOpen ? <Minus size={14} /> : <Plus size={14} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="font-dm-sans text-gray-500 text-sm leading-relaxed pb-5">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ({ lang }) {
  const tr = t[lang];
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-28 bg-[#FAFAF9]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Header */}
          <div className="lg:sticky lg:top-32">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="font-montserrat text-[#8C5E35] text-sm font-semibold tracking-widest uppercase mb-4"
            >
              {tr.label}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl md:text-5xl leading-tight"
              data-testid="faq-title"
            >
              {tr.title}{" "}
              <span className="text-[#8C5E35]">{tr.titleAccent}</span>
            </motion.h2>
          </div>

          {/* Right: Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-8 shadow-lg shadow-[#1F3A5E]/5 border border-[#D4B895]/20"
          >
            {tr.items.map((item, i) => (
              <FAQItem
                key={i}
                idx={i}
                item={item}
                isOpen={openIdx === i}
                toggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
